import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import { Zap, Copy, Check, X, Loader2 } from 'lucide-react';
import { cn } from '../lib/utils';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * <PromptPowerUp> — Reusable floating button that enhances selected text via AI.
 *
 * Usage:
 *   <PromptPowerUp contextType="general">
 *     <textarea value={...} onChange={...} />
 *   </PromptPowerUp>
 *
 * Props:
 *   contextType: 'general' | 'meeting' | 'email' | 'task' | 'report' | 'social_media' | 'technical'
 *   tone: 'professional' | 'friendly' | 'direct' | 'formal'
 *   onResult: (enhancedPrompt) => void — optional callback when result is ready
 */
export function PromptPowerUp({ children, contextType = 'general', tone = 'professional', onResult }) {
  const wrapperRef = useRef(null);
  const [selectedText, setSelectedText] = useState('');
  const [btnPos, setBtnPos] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setIsMobile(window.innerWidth < 768);
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Detect text selection within the wrapper
  const checkSelection = useCallback(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    // Check for textarea/input selection first
    const active = document.activeElement;
    if (active && wrapper.contains(active) && (active.tagName === 'TEXTAREA' || active.tagName === 'INPUT')) {
      const sel = active.value.substring(active.selectionStart, active.selectionEnd).trim();
      if (sel.length > 5) {
        const rect = active.getBoundingClientRect();
        if (isMobile) {
          setBtnPos({ bottom: true });
        } else {
          const lines = active.value.substring(0, active.selectionStart).split('\n');
          const lineH = 20;
          const top = rect.top + Math.min(lines.length * lineH, rect.height - 40) - 40;
          const left = rect.left + rect.width / 2;
          setBtnPos({ top, left });
        }
        setSelectedText(sel);
        return;
      }
    }

    // Check for contenteditable / regular selection
    const selection = window.getSelection();
    if (selection && selection.toString().trim().length > 5) {
      const range = selection.getRangeAt(0);
      if (wrapper.contains(range.commonAncestorContainer)) {
        const r = range.getBoundingClientRect();
        if (isMobile) {
          setBtnPos({ bottom: true });
        } else {
          setBtnPos({ top: r.top - 40, left: r.left + r.width / 2 });
        }
        setSelectedText(selection.toString().trim());
        return;
      }
    }

    setBtnPos(null);
    setSelectedText('');
  }, [isMobile]);

  useEffect(() => {
    document.addEventListener('selectionchange', checkSelection);
    return () => document.removeEventListener('selectionchange', checkSelection);
  }, [checkSelection]);

  const handlePowerUp = async () => {
    if (!selectedText || selectedText.length < 3) return;
    setLoading(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/api/prompt/powerup`, {
        selected_text: selectedText,
        context_type: contextType,
        output_language: 'English',
        tone,
      });

      if (res.data.success) {
        setResult(res.data.enhanced_prompt);
        onResult?.(res.data.enhanced_prompt);
        toast.success('Prompt enhanced!');
      } else {
        toast.error(res.data.error || 'Enhancement failed');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'PowerUp failed');
    }
    setLoading(false);
  };

  const handleCopy = () => {
    if (!result) return;
    navigator.clipboard.writeText(JSON.stringify(result, null, 2));
    setCopied(true);
    toast.success('Enhanced prompt copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleUse = () => {
    handleCopy();
    setResult(null);
    setBtnPos(null);
  };

  return (
    <div ref={wrapperRef} className="relative">
      {children}

      {/* Floating PowerUp button */}
      {btnPos && !loading && !result && (
        isMobile ? (
          /* Mobile: bottom sheet button */
          <div className="fixed bottom-20 left-4 right-4 z-50 flex justify-center" data-testid="powerup-float-mobile">
            <button
              onMouseDown={(e) => { e.preventDefault(); handlePowerUp(); }}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold text-slate-900 bg-gradient-to-r from-cyan-400 to-yellow-300 shadow-[0_0_24px_rgba(6,182,212,0.4)] active:scale-95 transition-transform"
            >
              <Zap className="h-4 w-4" />POWERUP SELECTION
            </button>
          </div>
        ) : (
          /* Desktop: floating above selection */
          <div
            className="fixed z-50"
            style={{ top: `${btnPos.top}px`, left: `${btnPos.left}px`, transform: 'translateX(-50%)' }}
            data-testid="powerup-float-desktop"
          >
            <button
              onMouseDown={(e) => { e.preventDefault(); handlePowerUp(); }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold text-slate-900 bg-gradient-to-r from-cyan-400 to-yellow-300 shadow-[0_0_20px_rgba(6,182,212,0.4)] hover:shadow-[0_0_30px_rgba(6,182,212,0.6)] transition-all"
            >
              <Zap className="h-3 w-3" />POWERUP
            </button>
          </div>
        )
      )}

      {/* Loading indicator */}
      {loading && btnPos && (
        <div className={cn("fixed z-50", isMobile ? "bottom-20 left-4 right-4 flex justify-center" : "")}
          style={!isMobile ? { top: `${btnPos.top}px`, left: `${btnPos.left}px`, transform: 'translateX(-50%)' } : undefined}
        >
          <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-800 border border-cyan-500/30 text-cyan-300 text-xs">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />Enhancing...
          </div>
        </div>
      )}

      {/* Result popup */}
      {result && (
        <div className={cn("fixed z-50", isMobile ? "bottom-0 left-0 right-0 p-3" : "")}
          style={!isMobile && btnPos ? { top: `${Math.max(btnPos.top - 10, 60)}px`, left: `${btnPos.left}px`, transform: 'translateX(-50%)' } : undefined}
        >
          <Card className="bg-slate-900 border-cyan-500/30 shadow-[0_0_30px_rgba(6,182,212,0.15)] max-w-md w-full" data-testid="powerup-result-popup">
            <CardContent className="p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] uppercase tracking-wider text-cyan-400 font-medium flex items-center gap-1">
                  <Zap className="h-3 w-3" />PowerUp Result
                </span>
                <button onClick={() => { setResult(null); setBtnPos(null); }} className="text-slate-500 hover:text-slate-300">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              <div className="bg-[#0d1117] rounded-lg p-2 max-h-48 overflow-y-auto mb-2">
                <pre className="text-[10px] text-slate-300 whitespace-pre-wrap break-words" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                  {JSON.stringify(result, null, 2).substring(0, 800)}
                  {JSON.stringify(result, null, 2).length > 800 ? '...' : ''}
                </pre>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="border-slate-700 text-slate-300 text-[10px] h-7 gap-1 flex-1" onClick={handleCopy}>
                  {copied ? <Check className="h-2.5 w-2.5 text-emerald-400" /> : <Copy className="h-2.5 w-2.5" />}
                  Copy JSON
                </Button>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500 text-[10px] h-7 gap-1 flex-1" onClick={handleUse}>
                  <Check className="h-2.5 w-2.5" />Use This
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
